<template>
  <div>
    <el-card style="margin-bottom: 12px;">
      <el-space>
        <span style="color:#6b7280;">统计窗口</span>
        <el-select v-model="days" style="width: 120px;" @change="loadKpis">
          <el-option :value="7" label="近7天" />
          <el-option :value="14" label="近14天" />
          <el-option :value="30" label="近30天" />
        </el-select>
      </el-space>
    </el-card>

    <el-row :gutter="12">
      <el-col v-for="item in statItems" :key="item.label" :xs="24" :sm="12" :md="8" :lg="6">
        <el-card shadow="hover" style="margin-bottom: 12px;">
          <div style="color: #6b7280; font-size: 13px;">{{ item.label }}</div>
          <div style="margin-top: 8px; font-size: 26px; font-weight: 700;">{{ item.value }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-card style="margin-top: 12px;">
      <template #header>KPI趋势</template>
      <el-table :data="trendRows" size="small">
        <el-table-column prop="day" label="日期" width="120" />
        <el-table-column prop="d1" label="D1留存" width="120" />
        <el-table-column prop="firstChat" label="首聊转化(10m)" width="140" />
        <el-table-column prop="proactive" label="主动回复(24h)" width="140" />
      </el-table>
    </el-card>

    <el-card style="margin-top: 12px;">
      <template #header>角色质量评分（低分优先）</template>
      <el-table :data="lowQualityRows" size="small">
        <el-table-column prop="role_name" label="角色" min-width="140" />
        <el-table-column prop="quality_score" label="综合分" width="90">
          <template #default="{ row }">{{ percent(row.quality_score) }}</template>
        </el-table-column>
        <el-table-column prop="reply_rate" label="回复率" width="90">
          <template #default="{ row }">{{ percent(row.reply_rate) }}</template>
        </el-table-column>
        <el-table-column prop="rechat_rate" label="复聊率" width="90">
          <template #default="{ row }">{{ percent(row.rechat_rate) }}</template>
        </el-table-column>
        <el-table-column prop="complaint_rate" label="投诉率" width="90">
          <template #default="{ row }">{{ percent(row.complaint_rate) }}</template>
        </el-table-column>
        <el-table-column prop="suggestion" label="建议" min-width="180" />
      </el-table>
    </el-card>

    <el-card style="margin-top: 12px;">
      <template #header>运营建议</template>
      <el-space wrap>
        <router-link to="/roles"><el-button>优化角色提示词</el-button></router-link>
        <router-link to="/sessions"><el-button>查看活跃会话</el-button></router-link>
        <router-link to="/follow-ups"><el-button>处理主动任务</el-button></router-link>
      </el-space>
      <div style="margin-top: 12px; color: #6b7280;">建议优先维护角色描述、语气规范和边界规则，提升角色扮演稳定性。</div>
    </el-card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { adminApi } from '../api/admin'

const raw = ref({})
const kpis = ref({})
const roleQuality = ref([])
const days = ref(7)
const percent = (v) => `${((Number(v) || 0) * 100).toFixed(1)}%`

const statItems = computed(() => [
  { label: '用户总数', value: raw.value.users || 0 },
  { label: '角色总数', value: raw.value.roles || 0 },
  { label: '会话总数', value: raw.value.sessions || 0 },
  { label: '消息总数', value: raw.value.messages || 0 },
  { label: '待发送任务', value: raw.value.follow_up_pending || 0 },
  { label: '已发送任务', value: raw.value.follow_up_sent || 0 },
  { label: '已取消任务', value: raw.value.follow_up_cancelled || 0 },
  { label: '24h触达数', value: raw.value.follow_up_sent_24h || 0 },
  { label: '主动触达命中率', value: percent(raw.value.follow_up_hit_rate) },
  { label: '主动触达取消率', value: percent(raw.value.follow_up_cancel_rate) },
  { label: 'D1留存率', value: percent(kpis.value?.d1_retention?.rate) },
  { label: '首聊转化率(10m)', value: percent(kpis.value?.first_chat_conversion_10m?.rate) },
  { label: '主动回复率(24h)', value: percent(kpis.value?.proactive_reply_rate_24h?.rate) }
])

const trendRows = computed(() => {
  const d1 = kpis.value?.trends?.d1_retention || []
  const first = kpis.value?.trends?.first_chat_conversion_10m || []
  const pro = kpis.value?.trends?.proactive_reply_rate_24h || []
  const map = new Map()
  d1.forEach((row) => map.set(row.day, { day: row.day, d1: percent(row.rate), firstChat: '-', proactive: '-' }))
  first.forEach((row) => {
    const cur = map.get(row.day) || { day: row.day, d1: '-', firstChat: '-', proactive: '-' }
    cur.firstChat = percent(row.rate)
    map.set(row.day, cur)
  })
  pro.forEach((row) => {
    const cur = map.get(row.day) || { day: row.day, d1: '-', firstChat: '-', proactive: '-' }
    cur.proactive = percent(row.rate)
    map.set(row.day, cur)
  })
  return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day))
})

const loadKpis = async () => {
  try {
    kpis.value = await adminApi.kpis({ days: days.value })
    const quality = await adminApi.roleQuality({ days: days.value })
    roleQuality.value = quality.list || []
  } catch (e) {
    ElMessage.error(e.message || 'KPI加载失败')
  }
}

const lowQualityRows = computed(() =>
  [...(roleQuality.value || [])]
    .sort((a, b) => Number(a.quality_score || 0) - Number(b.quality_score || 0))
    .slice(0, 8)
)

onMounted(async () => {
  try {
    raw.value = await adminApi.overview()
    await loadKpis()
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  }
})
</script>
